import { useEffect, useRef, useState } from 'react';
import {
  calcularMovimientos,
  instantanea,
  type FilaConPuesto,
  type Movimiento,
} from './movimiento.ts';

export interface EstadoMovimientos {
  /** Quién ha subido o bajado respecto a la carga anterior. Vacío en la primera. */
  movimientos: Map<string, Movimiento>;
  /**
   * `false` en la primera pintada. Es lo que apaga la animación de entrada: no es que
   * nadie se haya movido, es que todavía no hay con qué comparar.
   *
   * Pasa a `true` en cuanto existe una instantánea previa —aunque nadie se haya movido
   * aún— y ese matiz importa: Framer necesita haber medido las posiciones ANTES del
   * cambio para poder animarlo. Si se encendiera solo al detectar el primer movimiento,
   * ese primer movimiento sería justo el que no se vería.
   */
  animar: boolean;
}

const INICIAL: EstadoMovimientos = { movimientos: new Map(), animar: false };

/**
 * Compara la clasificación con la carga ANTERIOR de esta misma pantalla.
 *
 * La instantánea vive en un `ref`, no en el estado, y es deliberado: al desmontar el
 * componente —navegar a otra pantalla y volver— se pierde, y se vuelve a estar en
 * "primera carga". Es lo que se quiere. Entrar en la clasificación no puede animar
 * cambios de hace media hora como si acabaran de ocurrir; solo tiene sentido animar lo
 * que se mueve mientras el usuario está mirando.
 */
export function useMovimientos(filas: readonly FilaConPuesto[] | undefined): EstadoMovimientos {
  const previos = useRef<Map<string, number> | null>(null);
  const [estado, setEstado] = useState<EstadoMovimientos>(INICIAL);

  useEffect(() => {
    if (!filas) return;
    const movimientos = calcularMovimientos(filas, previos.current);
    previos.current = instantanea(filas);
    setEstado({ movimientos, animar: true });
  }, [filas]);

  return estado;
}
